import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Popconfirm, Row, Space, Table, Typography, notification, Card, Tooltip } from 'antd';
import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';
import dayjs from 'dayjs';
import { APP_DATE_FORMAT } from 'config/constants';
import { listMeasuringInstrumentInspectors, deleteMeasuringInstrumentInspector, resendMeasuringInstrumentInspector } from '../services/measuring-instrument-inspectorService';
import MeasuringInstrumentInspectorForm from './Form';
import { getMeasuringInstrumentInventory } from '../../measuring-instrument-inventories/services/measuring-instrument-inventoryService';
import { RefreshIcon } from 'hugeicons-react';

export default function MeasuringInstrumentInspectorList({ referenceId }: { referenceId?: number }) {
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [notificationApi, contextHolder] = notification.useNotification();
  const [hasPendingApproval, setHasPendingApproval] = useState(false);
  const [inventoryOwnerId, setInventoryOwnerId] = useState<number | undefined>(undefined);
  const [resendLoadingId, setResendLoadingId] = useState<number | null>(null);

  const fetchData = async () => {
    const res = await listMeasuringInstrumentInspectors(page - 1, pageSize, 'assignmentAt,desc', referenceId !== undefined ? 'measuringInstrumentInventoryId.equals=' + referenceId : '');
    setData(res.content);
    setTotal(res.totalElements);
    setHasPendingApproval(Array.isArray(res.content) && res.content.some(item => !item?.confirmedAt && !item?.rejectedAt));
  };
  const latestAssignmentId = useMemo(() => {
    if (!Array.isArray(data) || !data.length) return undefined;
    const sorted = [...data].sort((a: any, b: any) => {
      const aTime = a?.assignmentAt ? dayjs(a.assignmentAt).valueOf() : (a?.createdAt ? dayjs(a.createdAt).valueOf() : 0);
      const bTime = b?.assignmentAt ? dayjs(b.assignmentAt).valueOf() : (b?.createdAt ? dayjs(b.createdAt).valueOf() : 0);
      return bTime - aTime;
    });
    return sorted[0]?.id;
  }, [data]);
  useEffect(() => { if (referenceId !== undefined) fetchData(); }, [page, pageSize, referenceId]);
  useEffect(() => {
    if (referenceId) {
      getMeasuringInstrumentInventory(referenceId)
        .then((res: any) => setInventoryOwnerId(res?.account?.id ?? undefined))
        .catch(() => setInventoryOwnerId(undefined));
    } else {
      setInventoryOwnerId(undefined);
    }
  }, [referenceId]);
  const excludedAccountId = useMemo(() => inventoryOwnerId, [inventoryOwnerId]);
  const openCreate = () => { setEditingId(undefined); setOpen(true); };
  const openEdit = (id: number) => { setEditingId(id); setOpen(true); };
  const handleClose = () => setOpen(false);
  const handleDelete = async (id: number) => {
    try {
      await deleteMeasuringInstrumentInspector(id);
      notificationApi.success({ message: 'Ölçüm Cihazı P.K.M Uzmanı Silindi' });
      fetchData();
    } catch (e) {
      notificationApi.error({ message: 'Ölçüm Cihazı P.K.M Uzmanı silme işlemi başarısız' });
    }
  };
  const handleResend = async (id: number) => {
    setResendLoadingId(id);
    try {
      await resendMeasuringInstrumentInspector(id);
      notificationApi.success({ message: 'Atama tekrar gönderildi' });
      await fetchData();
    } catch {
      notificationApi.error({ message: 'Atama tekrar gönderilemedi' });
    } finally {
      setResendLoadingId(null);
    }
  };
  const columns = [
    {
      title: 'Kullanıcı', dataIndex: 'account.firstName', sorter: true, render: (_: any, r: any) => {
        const acc = r?.account;
        if (!acc) return '';
        const fullName = [acc?.firstName, acc?.lastName].filter(Boolean).join(' ');
        return (
          <div>
            <div>{fullName}</div>
            <Typography.Text type='secondary'>{acc?.email}</Typography.Text>
          </div>
        );
      }
    },
    // { title: 'Ölçüm Cihazı', dataIndex: 'measuringInstrumentInventory.measuringInstrument.name', sorter: true, render: (_: any, r: any) => {
    //   const inv = r?.measuringInstrumentInventory;
    //   const name = inv?.measuringInstrument?.name;
    //   const serial = inv?.serialNumber;
    //   if (!name && !serial) return '';
    //   return (
    //     <div>
    //       <div>{name}</div>
    //       <Typography.Text type='secondary'>{serial}</Typography.Text>
    //     </div>
    //   );
    // } },
    { title: 'Teslim Tarihi', dataIndex: 'assignmentAt', sorter: true, render: (v: string) => v ? dayjs(v).format(APP_DATE_FORMAT) : '' },
    { title: 'Onay Tarihi', dataIndex: 'confirmedAt', sorter: true, render: (v: string) => v ? dayjs(v).format(APP_DATE_FORMAT) : '' },
    { title: 'Ret Tarihi', dataIndex: 'rejectedAt', sorter: true, render: (v: string) => v ? dayjs(v).format(APP_DATE_FORMAT) : '' },
    { title: 'Ret Sebebi', dataIndex: 'rejectionReason', render: (v: string) => v || '' },
    {
      title: 'İşlemler', render: (_: any, r: any) => {
        const disabled = !!r?.confirmedAt || !!r?.rejectedAt;
        return (
          <Space>
            <Tooltip title='Düzenle'>
              <Button
                icon={<img src={PenIcon} alt="pen" className="icon" />}
                shape="circle"
                onClick={() => openEdit(r.id)}
                className="icon-button ml-12px"
                disabled={disabled}
              />
            </Tooltip>
            <Popconfirm title='Sil?' onConfirm={() => handleDelete(r.id)} disabled={disabled}>
              <Tooltip title='Sil'>
                <Button
                  icon={<img src={TrashIcon} alt="trash" className="icon" />}
                  shape="circle"
                  className="icon-button ml-12px"
                  disabled={disabled}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];
  return (
    <>
      {contextHolder}
      <Card
        className='br-30px'
        title={<Typography.Title level={4} style={{ margin: 0 }}>P.K.M Uzmanları</Typography.Title>}
        extra={
          <Tooltip title={hasPendingApproval ? 'Onaylanmayan kayıt mevcut. Silindikten sonra yeni kayıt eklenebilir.' : undefined}>
            <span>
              <Button type='primary' onClick={openCreate} disabled={hasPendingApproval}>
                Ekle
              </Button>
            </span>
          </Tooltip>
        }
      >
        <Table
          rowKey='id'
          dataSource={data}
          columns={columns}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['50', '100', '200', '500'],
            onChange: (p, s) => { setPage(p); setPageSize(s); }
          }}
        />
      </Card>
      <Drawer open={open} onClose={handleClose} destroyOnClose width={480} title={editingId ? 'Ölçüm Cihazı Teslim Bilgilerini Düzenle' : 'Ölçüm Cihazı Teslim Bilgileri'}>
        <MeasuringInstrumentInspectorForm
          id={editingId}
          onSuccess={() => { handleClose(); fetchData(); }}
          referenceId={referenceId}
          existingAssignments={data}
          excludedAccountId={excludedAccountId}
        />
      </Drawer>
    </>
  );
}
